import React, { useEffect, useState } from "react";
import { api, API_BASE, fmtNumber } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Section } from "@/components/Primitives";
import {
  UploadSimple, CheckCircle, WarningCircle, Clock, CloudArrowUp,
  DownloadSimple, Eye, Trash, Warning, TrendUp, X,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const Upload = () => {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("append");
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);

  // Danger-zone clear flow
  const [clearOpen, setClearOpen] = useState(false);
  const [clearPhrase, setClearPhrase] = useState("");
  const [clearScope, setClearScope] = useState("everything");
  const [clearBusy, setClearBusy] = useState(false);

  const isSuperAdmin = user?.role === "super_admin";

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/uploads/history");
      setHistory(data || []);
    } catch { /* role restricted */ }
  };

  useEffect(() => { loadHistory(); }, []);

  // Poll history every 2.5s while any row is queued/processing — gives the
  // user live progress on long ingestions without manual refresh.
  useEffect(() => {
    const inFlight = (history || []).some((h) => h.status === "queued" || h.status === "processing");
    if (!inFlight) return;
    const t = setInterval(loadHistory, 2500);
    return () => clearInterval(t);
  }, [history]);

  const doPreview = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/preview", fd);
      setPreview(data);
      if (data.missing_columns?.length) {
        toast.warning(`Preview ready but ${data.missing_columns.length} required column(s) missing`);
      } else {
        toast.success(`Preview ready — ${fmtNumber(data.total_rows)} rows · ready to confirm`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setBusy(false); }
  };

  const doIngest = async () => {
    if (!file || !preview) return;
    if (preview.missing_columns?.length) {
      toast.error(`Cannot upload — missing required columns: ${preview.missing_columns.join(", ")}`);
      return;
    }
    setBusy(true); setProgress("uploading");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      // Generous client-side timeout so a slow proxy on a 300K-row file
      // doesn't kill the request before the server has even queued the job.
      const { data } = await api.post("/uploads/ingest", fd, { timeout: 120000 });
      if (data.queued) {
        toast.success(
          data.message ||
            "Your file has been received and is being processed in the background. The Recent uploads table below will show progress and final inserted / skipped / duplicate counts.",
          { duration: 8000 },
        );
      } else if (data.inserted != null) {
        toast.success(`Imported ${fmtNumber(data.inserted)} rows (skipped ${fmtNumber(data.skipped || 0)})`);
      }
      setPreview(null); setFile(null);
      await loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Upload failed");
    } finally { setBusy(false); setProgress(null); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await fetch(`${API_BASE}/uploads/template`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sales_upload_template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded — open it, fill your rows, then upload.");
    } catch (e) {
      toast.error("Failed to download template");
    }
  };

  const [recomputeBusy, setRecomputeBusy] = useState(false);
  const [normalizeBusy, setNormalizeBusy] = useState(false);
  const [normalizePreview, setNormalizePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [resetRefsBusy, setResetRefsBusy] = useState(false);

  const doResetReferenceDropdowns = async () => {
    if (!window.confirm(
      "Reset reference dropdowns?\n\n" +
      "This deletes any brand / location / salesman / company / category " +
      "entry that no longer has a backing sales transaction. Use this to " +
      "clear stale dropdown options (e.g. ghost \".0\" codes left over " +
      "from before a previous wipe).\n\n" +
      "Sales transactions, targets and uploads are NOT touched."
    )) return;
    setResetRefsBusy(true);
    try {
      const { data } = await api.post("/uploads/reset-reference-dropdowns");
      const d = data?.deleted || {};
      const total = Object.values(d).reduce((a, b) => a + (Number(b) || 0), 0);
      toast.success(
        `Removed ${fmtNumber(total)} stale dropdown entries ` +
        `(brands ${d.brands || 0}, locations ${d.locations || 0}, ` +
        `salesmen ${d.salesmen || 0}, companies ${d.companies || 0}, ` +
        `categories ${d.categories || 0}). Reloading…`,
        { duration: 4000 },
      );
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed.");
    } finally {
      setResetRefsBusy(false);
    }
  };

  // Two-step flow: clicking "Clean codes" first OPENS a preview modal that
  // shows what would change. The actual normalize only runs when the user
  // hits the modal's Confirm button.
  const openNormalizePreview = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await api.get("/uploads/normalize-preview");
      setNormalizePreview(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load preview.");
    } finally {
      setPreviewLoading(false);
    }
  };
  const closeNormalizePreview = () => setNormalizePreview(null);

  const doNormalizeCodes = async () => {
    setNormalizePreview(null);
    setNormalizeBusy(true);
    try {
      await api.post("/uploads/normalize-identifier-codes");
      toast.message("Code cleanup queued. Polling for completion…", { duration: 3000 });
      const started = Date.now();
      let final = null;
      while (Date.now() - started < 5 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: st } = await api.get("/uploads/normalize-status");
        if (st.status === "complete") { final = st; break; }
        if (st.status === "failed") {
          toast.error(`Normalize failed: ${st.error || "unknown error"}`);
          return;
        }
      }
      if (!final) { toast.error("Normalize timed out after 5 min."); return; }
      toast.success(
        `Normalized ${fmtNumber(final.rows_updated || 0)} rows across ${(final.fields_done || []).length} fields. Reloading…`,
        { duration: 4000 },
      );
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Normalize failed.");
    } finally {
      setNormalizeBusy(false);
    }
  };

  const doDeleteUpload = async (h) => {
    const inserted = h.inserted ?? 0;
    const duplicates = h.duplicates ?? 0;
    const totalSeen = inserted + duplicates;
    const dedupNote = duplicates > 0
      ? `\n\n⚠ This file had ${fmtNumber(duplicates)} duplicate row(s) that were ` +
        `already in the database from a PREVIOUS upload. Those rows are owned by ` +
        `that earlier upload and will NOT be removed (deleting them here would ` +
        `corrupt the original upload).\n` +
        `\nOnly the ${fmtNumber(inserted)} row(s) actually inserted by this file ` +
        `(out of ${fmtNumber(totalSeen)} total in the file) will be deleted.`
      : "";
    const confirmed = window.confirm(
      `Delete upload "${h.file_name}"?\n\n` +
      `Permanently removes ${fmtNumber(inserted)} transaction(s) ingested from this ` +
      `file and the upload-history record. Targets, brands, locations and salesmen ` +
      `are preserved.${dedupNote}\n\n` +
      `(Older uploads created before per-row tagging will report 0 rows but the ` +
      `history card is still removed.)`,
    );
    if (!confirmed) return;
    setDeletingId(h.id);
    try {
      const { data } = await api.delete(`/uploads/${h.id}`);
      toast.success(
        `Removed "${data.filename || h.file_name}" — ${fmtNumber(data.rows_deleted)} transaction${data.rows_deleted === 1 ? "" : "s"} deleted.`,
        { duration: 4500 },
      );
      await loadHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    } finally {
      setDeletingId(null);
    }
  };

  const doRecomputeGP = async () => {
    setRecomputeBusy(true);
    try {
      const { data: kicked } = await api.post("/uploads/recompute-gross-profit");
      if (kicked.status === "running") {
        toast.message("Recompute already running. Waiting for it to finish…", { duration: 4000 });
      } else {
        toast.message(
          `Recompute queued · ~${fmtNumber(kicked.rows_total || 0)} rows. This runs in the background — please wait…`,
          { duration: 4000 },
        );
      }
      // Poll the status endpoint until complete or failed (max 5 min).
      const started = Date.now();
      let finalState = null;
      while (Date.now() - started < 5 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: state } = await api.get("/uploads/recompute-status");
        if (state.status === "complete") { finalState = state; break; }
        if (state.status === "failed") {
          toast.error(`Recompute failed: ${state.error || "unknown error"}`);
          return;
        }
      }
      if (!finalState) {
        toast.error("Recompute timed out after 5 min. Refresh the page and check the data.");
        return;
      }
      const delta = finalState.delta ?? 0;
      toast.success(
        `Recomputed GP across ${fmtNumber(finalState.rows_updated || 0)} of ${fmtNumber(finalState.rows_total || 0)} rows. ` +
        `Total GP: ${fmtNumber(finalState.gp_before)} → ${fmtNumber(finalState.gp_after)} KWD ` +
        `(${delta >= 0 ? "+" : ""}${fmtNumber(delta)}). Reloading…`,
        { duration: 5000 },
      );
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Recompute failed.");
    } finally {
      setRecomputeBusy(false);
    }
  };

  const doClearSales = async () => {
    if (clearPhrase !== "CLEAR ALL SALES") {
      toast.error("Type CLEAR ALL SALES to confirm.");
      return;
    }
    setClearBusy(true);
    try {
      const fd = new FormData();
      fd.append("confirm", clearPhrase);
      fd.append("scope", clearScope);
      const { data } = await api.post("/uploads/clear-sales-data", fd);
      const parts = [
        `${fmtNumber(data.sales_transactions_deleted)} transactions`,
        `${fmtNumber(data.upload_history_deleted)} history rows`,
      ];
      if (data.scope === "everything") {
        parts.push(
          `${fmtNumber(data.targets_deleted || 0)} targets`,
          `${fmtNumber(data.brands_deleted || 0)} brands`,
          `${fmtNumber(data.locations_deleted || 0)} locations`,
          `${fmtNumber(data.salesmen_deleted || 0)} salesmen`,
        );
      }
      toast.success(
        `Wiped ${parts.join(" + ")}. ${data.scope === "everything"
          ? "Reference collections will rebuild from your next upload. Reloading…"
          : "Targets preserved. Reloading…"}`,
      );
      setClearOpen(false);
      setClearPhrase("");
      // Force a full page reload so every cached chart/filter selection
      // clears out — without this, components like the brand-by-location
      // donut keep stale ids in state and trigger weird empty / 404-ish
      // renders against rebuilt reference data.
      setTimeout(() => { window.location.reload(); }, 700);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Clear failed";
      toast.error(typeof detail === "string" ? detail : JSON.stringify(detail));
      setClearBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Upload Center"
        title="Feed the ledger"
        subtitle="Load Excel or CSV files of daily sales. Append to extend history without duplicates, or replace to reset the master record."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card testId="upload-dropzone" className="lg:col-span-2">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="text-[10px] uppercase tracking-[0.25em] text-myb-500 font-semibold">Step 1 · Choose a file</div>
            <button
              data-testid="upload-download-template"
              onClick={downloadTemplate}
              className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 border border-myb-500/40 text-myb-500 hover:bg-myb-50 rounded-sm font-semibold uppercase tracking-wider"
            >
              <DownloadSimple size={13} weight="bold" /> Sample template
            </button>
          </div>

          <label htmlFor="upload-file" className="flex flex-col items-center justify-center py-10 border-2 border-dashed border-myb-100 hover:border-myb-500 rounded-sm cursor-pointer transition-all">
            <CloudArrowUp weight="duotone" size={48} className="text-myb-500/70 mb-3" />
            <div className="font-display text-2xl">{file ? file.name : "Drop Excel / CSV here"}</div>
            <div className="text-xs text-muted-foreground mt-1">.xlsx, .xls, .csv · max 100 MB</div>
            <input id="upload-file" data-testid="upload-file-input" type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} />
          </label>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Mode</span>
              <div className="flex border border-myb-100 rounded-sm overflow-hidden">
                {["append", "replace"].map((m) => (
                  <button
                    key={m}
                    data-testid={`upload-mode-${m}`}
                    onClick={() => setMode(m)}
                    className={`text-xs px-4 py-2 uppercase tracking-wider transition-all ${
                      mode === m ? "bg-myb-500 text-white" : "text-muted-foreground hover:bg-myb-50/70"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
            <div className="ml-auto flex gap-2">
              <button
                data-testid="upload-preview-btn"
                disabled={!file || busy} onClick={doPreview}
                className="text-sm border border-myb-500/40 text-myb-500 hover:bg-myb-50 px-4 py-2 rounded-sm disabled:opacity-40 inline-flex items-center gap-2 font-semibold"
              >
                <Eye size={14} /> {preview ? "Re-preview" : "Preview"}
              </button>
              <button
                data-testid="upload-ingest-btn"
                disabled={!file || !preview || busy || (preview?.missing_columns?.length > 0)}
                onClick={doIngest}
                title={!preview ? "Preview the file first" : (preview?.missing_columns?.length > 0 ? `Missing columns: ${preview.missing_columns.join(", ")}` : "Commit the upload")}
                className="text-sm bg-myb-500 text-white hover:bg-myb-400 px-4 py-2 rounded-sm disabled:opacity-40 inline-flex items-center gap-2 font-semibold"
              >
                <UploadSimple size={14} /> {busy && progress === "uploading" ? "Importing…" : "Confirm upload"}
              </button>
            </div>
          </div>

          {!preview && file && (
            <div data-testid="upload-hint-preview" className="mt-4 text-xs text-muted-foreground bg-myb-50/50 border hairline rounded-sm px-3 py-2">
              Preview the file to verify the rows look correct before confirming the upload.
            </div>
          )}

          {preview && (
            <div data-testid="upload-preview" className="mt-6 border-t hairline pt-5">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="text-[10px] uppercase tracking-[0.25em] text-myb-500 font-semibold">
                  Step 2 · Preview · {fmtNumber(preview.total_rows)} total rows · showing first {Math.min(10, preview.rows.length)}
                </div>
                {preview.missing_columns?.length > 0 ? (
                  <span data-testid="upload-missing-cols" className="text-[10px] uppercase tracking-wider px-2 py-1 bg-rose-50 text-rose-600 border border-rose-200 rounded-sm font-semibold inline-flex items-center gap-1">
                    <WarningCircle size={12} /> Missing: {preview.missing_columns.join(", ")}
                  </span>
                ) : (
                  <span className="text-[10px] uppercase tracking-wider px-2 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm font-semibold inline-flex items-center gap-1">
                    <CheckCircle size={12} /> Schema OK — ready to confirm
                  </span>
                )}
              </div>
              {preview.extra_columns?.length > 0 && (
                <div className="text-[11px] text-amber-600 mb-2">
                  Note: these extra columns will be ignored — {preview.extra_columns.join(", ")}
                </div>
              )}
              <div className="overflow-auto max-h-80 border border-myb-100 rounded-sm scrollbar-thin">
                <table className="w-full text-xs">
                  <thead className="bg-white/95 sticky top-0">
                    <tr>
                      {preview.columns.map((c) => (
                        <th key={c} className="text-left px-2 py-1.5 text-myb-500 font-normal text-[10px] uppercase tracking-wider whitespace-nowrap">{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r, i) => (
                      <tr key={i} className="odd:bg-myb-50/40">
                        {preview.columns.map((c) => (
                          <td key={c} className="px-2 py-1.5 border-t border-myb-100/60 whitespace-nowrap">
                            {String(r[c] ?? "")}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Card>

        <Card title="Upload rules" className="text-sm">
          <ul className="space-y-3 text-muted-foreground leading-relaxed">
            <li><span className="text-myb-500 font-semibold">1. Download the template</span> — click "Sample template" above. It contains the exact headers (Sales sheet) plus an Instructions sheet describing each column.</li>
            <li><span className="text-myb-500 font-semibold">2. Fill your rows</span> — use one row per transaction. All amounts in KWD.</li>
            <li><span className="text-myb-500 font-semibold">3. Upload & preview</span> — the first 10 rows will display so you can verify before committing.</li>
            <li><span className="text-myb-500 font-semibold">4. Confirm</span> — <span className="text-myb-500">Append</span> adds new rows (bill+item+date dedup). <span className="text-myb-500">Replace</span> wipes all transactions first.</li>
          </ul>
        </Card>
      </div>

      <Section eyebrow="History" title="Recent uploads">
        <Card testId="upload-history">
          {history.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">No uploads yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b hairline">
                  {["Status", "File", "Mode", "Progress", "Inserted", "Skipped", "Duplicates", "By", "When", ""].map((h, i) => (
                    <th key={`${h}-${i}`} className="py-2 px-3 text-[10px] uppercase tracking-[0.2em] text-myb-500 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map((h) => {
                  const pct =
                    h.total_rows && h.total_rows > 0
                      ? Math.min(100, Math.round(((h.processed_rows || 0) / h.total_rows) * 100))
                      : null;
                  return (
                    <tr key={h.id} className="border-t hairline align-top" data-testid={`upload-row-${h.id}`}>
                      <td className="py-2 px-3 whitespace-nowrap">
                        {h.status === "success" && <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold"><CheckCircle size={14} /> Success</span>}
                        {h.status === "failed" && <span className="inline-flex items-center gap-1 text-rose-600 text-xs font-semibold" title={h.error || ""}><WarningCircle size={14} /> Failed</span>}
                        {h.status === "processing" && <span className="inline-flex items-center gap-1 text-amber-600 text-xs font-semibold"><Clock size={14} /> Processing</span>}
                        {h.status === "queued" && <span className="inline-flex items-center gap-1 text-myb-500 text-xs font-semibold"><Clock size={14} /> Queued</span>}
                      </td>
                      <td className="py-2 px-3 max-w-[260px] truncate" title={h.file_name}>{h.file_name}</td>
                      <td className="py-2 px-3 uppercase text-xs">{h.mode}</td>
                      <td className="py-2 px-3 min-w-[160px]">
                        {pct == null ? (
                          <span className="text-xs text-muted-foreground">—</span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-myb-50 rounded-full overflow-hidden max-w-[120px]">
                              <div
                                className={`h-full ${h.status === "success" ? "bg-emerald-500" : h.status === "failed" ? "bg-rose-500" : "bg-amber-500"}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className="text-[11px] tabular-nums whitespace-nowrap">
                              {fmtNumber(h.processed_rows || 0)} / {fmtNumber(h.total_rows)} ({pct}%)
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-3 tabular-nums text-emerald-700 font-medium">{fmtNumber(h.inserted ?? h.rows_count)}</td>
                      <td className="py-2 px-3 tabular-nums text-muted-foreground">{fmtNumber(h.skipped || 0)}</td>
                      <td className="py-2 px-3 tabular-nums text-muted-foreground">{fmtNumber(h.duplicates || 0)}</td>
                      <td className="py-2 px-3 text-muted-foreground">{h.uploaded_by}</td>
                      <td className="py-2 px-3 text-muted-foreground text-xs whitespace-nowrap">{new Date(h.uploaded_at).toLocaleString()}</td>
                      <td className="py-2 px-3 whitespace-nowrap">
                        {(isSuperAdmin || user?.role === "management") && h.status !== "queued" && h.status !== "processing" && (
                          <button
                            data-testid={`delete-upload-${h.id}`}
                            onClick={() => doDeleteUpload(h)}
                            disabled={deletingId === h.id}
                            title="Revert this upload (deletes its rows + history record)"
                            className="text-rose-600 hover:text-rose-800 disabled:opacity-40 inline-flex items-center gap-1 text-xs font-semibold"
                          ><Trash size={12} weight="bold" /> {deletingId === h.id ? "Deleting…" : "Delete"}</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {history.some((h) => h.status === "failed" && h.error) && (
            <div className="mt-3 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded-sm p-2.5">
              {history.filter((h) => h.status === "failed" && h.error).slice(0, 1).map((h) => (
                <div key={h.id}><span className="font-semibold">Latest failure ({h.file_name}):</span> {h.error}</div>
              ))}
            </div>
          )}
        </Card>
      </Section>

      {isSuperAdmin && (
        <Section eyebrow="Data integrity" title="Recompute Gross Profit">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card testId="recompute-gp-card" className="!bg-myb-50/40 border-myb-200">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-start gap-3">
                  <TrendUp size={24} weight="duotone" className="text-myb-500 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-display text-lg text-myb-500">Recalculate GP from net_value − cogs</div>
                    <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      The uploaded file's Gross Profit column is unreliable — for a few thousand
                      edge-case rows (100%-discount giveaways, returns, data-entry slips) it
                      stores a positive value where the true accounting figure is negative.
                      Clicking this recomputes <code className="text-myb-500 font-semibold">gross_profit = net_value − cogs</code>{" "}
                      for every transaction. Runs in the background and is safe to rerun.
                    </div>
                  </div>
                </div>
                <button
                  data-testid="recompute-gp-btn"
                  disabled={recomputeBusy}
                  onClick={doRecomputeGP}
                  className="text-sm bg-myb-500 hover:bg-myb-600 text-white px-4 py-2 rounded-sm inline-flex items-center gap-2 font-semibold whitespace-nowrap shrink-0 disabled:opacity-50"
                >
                  <TrendUp size={14} weight="bold" /> {recomputeBusy ? "Recomputing…" : "Recompute GP"}
                </button>
              </div>
            </Card>

            <Card testId="normalize-codes-card" className="!bg-amber-50/40 border-amber-200">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-start gap-3">
                  <Warning size={24} weight="duotone" className="text-amber-600 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-display text-lg text-amber-700">Strip ".0" from numeric codes</div>
                    <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      Excel often stores numeric IDs as floats so <code className="text-amber-700 font-semibold">"151"</code> arrives
                      as <code className="text-amber-700 font-semibold">"151.0"</code> — creating phantom duplicates in
                      Company, Location, Salesman, Item Code and Bill No. lookups. Cleanup
                      strips the suffix, merges the dups, and rebuilds reference tables.
                    </div>
                  </div>
                </div>
                <button
                  data-testid="normalize-codes-btn"
                  disabled={normalizeBusy || previewLoading}
                  onClick={openNormalizePreview}
                  className="text-sm bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-sm inline-flex items-center gap-2 font-semibold whitespace-nowrap shrink-0 disabled:opacity-50"
                >
                  <Warning size={14} weight="bold" /> {normalizeBusy ? "Cleaning…" : previewLoading ? "Loading…" : "Clean codes"}
                </button>
              </div>
            </Card>

            <Card testId="reset-refs-card" className="!bg-sky-50/40 border-sky-200">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-start gap-3">
                  <Warning size={24} weight="duotone" className="text-sky-600 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-display text-lg text-sky-700">Reset reference dropdowns</div>
                    <div className="text-xs text-muted-foreground mt-1 leading-relaxed max-w-2xl">
                      Removes any brand, location, salesman, company or category
                      entry that has no backing sales transaction. Use this when
                      dropdowns show ghost <code className="text-sky-700 font-semibold">.0</code> codes
                      left over from a previous wipe — including the case where
                      the sales ledger is already empty (this will then clear
                      every reference list). Sales transactions, targets and
                      upload history are not touched.
                    </div>
                  </div>
                </div>
                <button
                  data-testid="reset-refs-btn"
                  disabled={resetRefsBusy}
                  onClick={doResetReferenceDropdowns}
                  className="text-sm bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 rounded-sm inline-flex items-center gap-2 font-semibold whitespace-nowrap shrink-0 disabled:opacity-50"
                >
                  <Warning size={14} weight="bold" /> {resetRefsBusy ? "Resetting…" : "Reset dropdowns"}
                </button>
              </div>
            </Card>
          </div>
        </Section>
      )}

      {isSuperAdmin && (
        <Section eyebrow="Danger zone" title="Reset sales ledger">
          <Card testId="clear-sales-card" className="!bg-rose-50/40 border-rose-200">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-start gap-3">
                <Warning size={24} weight="duotone" className="text-rose-600 mt-0.5 shrink-0" />
                <div>
                  <div className="font-display text-lg text-rose-700">Clear all data</div>
                  <div className="text-xs text-muted-foreground mt-1 leading-relaxed max-w-2xl">
                    Wipes <span className="font-semibold text-rose-700">sales transactions, upload history, targets, brands, locations, salesmen, companies and categories</span>.
                    <span className="font-semibold text-myb-500"> Users, saved views, scope assignments and audit logs are preserved.</span>
                    Your next upload will repopulate every reference list from the file's contents.
                  </div>
                </div>
              </div>
              <button
                data-testid="clear-sales-open-btn"
                onClick={() => setClearOpen(true)}
                className="text-sm bg-rose-600 hover:bg-rose-500 text-white px-4 py-2 rounded-sm inline-flex items-center gap-2 font-semibold whitespace-nowrap shrink-0"
              >
                <Trash size={14} weight="bold" /> Clear all data
              </button>
            </div>
          </Card>

          {clearOpen && (
            <div
              data-testid="clear-sales-dialog"
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
              onClick={(e) => { if (e.target === e.currentTarget) setClearOpen(false); }}
            >
              <div className="bg-white rounded-sm shadow-2xl w-full max-w-lg border border-rose-200">
                <div className="px-6 py-4 border-b border-rose-100 flex items-center gap-3">
                  <Warning size={22} weight="duotone" className="text-rose-600" />
                  <div className="font-display text-xl text-rose-700">Confirm: wipe data</div>
                </div>
                <div className="px-6 py-5 space-y-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-myb-500 font-semibold mb-2">Scope</div>
                    <div className="flex border hairline rounded-sm overflow-hidden" data-testid="clear-scope-toggle">
                      {[
                        { k: "everything", label: "Everything (recommended)" },
                        { k: "sales_only", label: "Sales only · keep targets/refs" },
                      ].map(({ k, label }) => (
                        <button
                          key={k}
                          data-testid={`clear-scope-${k}`}
                          onClick={() => setClearScope(k)}
                          className={`flex-1 text-[11px] uppercase tracking-wider px-3 py-2 font-semibold transition-all ${
                            clearScope === k ? "bg-rose-600 text-white" : "text-muted-foreground hover:bg-rose-50"
                          }`}
                        >{label}</button>
                      ))}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
                      {clearScope === "everything" ? (
                        <>Will delete: <span className="font-semibold text-rose-700">sales transactions, upload history, targets, brands, locations, salesmen</span>. Reference collections rebuild from your next upload. Companies regenerate from sales rows.</>
                      ) : (
                        <>Will delete: <span className="font-semibold text-rose-700">sales transactions + upload history</span> only. Targets, brands, locations, salesmen <span className="font-semibold text-myb-500">stay intact</span>.</>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">Type <span className="font-mono bg-rose-50 px-1.5 py-0.5 rounded text-rose-700 border border-rose-200">CLEAR ALL SALES</span> below to confirm.</div>
                  <input
                    data-testid="clear-sales-phrase-input"
                    type="text"
                    value={clearPhrase}
                    onChange={(e) => setClearPhrase(e.target.value)}
                    placeholder="CLEAR ALL SALES"
                    autoFocus
                    className="w-full bg-white border border-rose-200 focus:border-rose-500 rounded-sm px-3 py-2 text-sm font-mono outline-none"
                  />
                </div>
                <div className="px-6 py-4 border-t border-rose-100 flex items-center justify-end gap-2">
                  <button
                    data-testid="clear-sales-cancel-btn"
                    onClick={() => { setClearOpen(false); setClearPhrase(""); }}
                    disabled={clearBusy}
                    className="text-sm px-4 py-2 border border-border rounded-sm text-muted-foreground hover:text-foreground"
                  >Cancel</button>
                  <button
                    data-testid="clear-sales-confirm-btn"
                    onClick={doClearSales}
                    disabled={clearBusy || clearPhrase !== "CLEAR ALL SALES"}
                    className="text-sm bg-rose-600 hover:bg-rose-500 text-white px-4 py-2 rounded-sm inline-flex items-center gap-2 font-semibold disabled:opacity-40"
                  >
                    <Trash size={14} weight="bold" /> {clearBusy ? "Wiping…" : "Yes, wipe"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </Section>
      )}

      {/* Code-normalization preview modal — shows EXACTLY which rows will
          change before the user pulls the trigger. Closed by default. */}
      {normalizePreview && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-myb-900/40 backdrop-blur-sm p-4"
          data-testid="normalize-preview-modal"
          onClick={(e) => { if (e.target === e.currentTarget) closeNormalizePreview(); }}
        >
          <div className="bg-white border hairline rounded-sm shadow-2xl max-w-3xl w-full max-h-[88vh] overflow-y-auto">
            <div className="flex items-start justify-between border-b hairline p-5 bg-amber-50/40">
              <div className="flex items-start gap-3">
                <Warning size={26} weight="duotone" className="text-amber-600 mt-0.5" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-amber-700 font-semibold">Confirm cleanup</div>
                  <div className="font-display text-xl text-amber-700 mt-0.5">Strip ".0" from numeric codes</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {normalizePreview.total_rows_affected > 0
                      ? <><span className="font-semibold text-amber-700">{fmtNumber(normalizePreview.total_rows_affected)}</span> total updates queued across {normalizePreview.fields.filter((f) => f.rows_to_update > 0).length} field(s). Review the sample below.</>
                      : "No dirty codes found — your data is already clean."}
                  </div>
                </div>
              </div>
              <button
                onClick={closeNormalizePreview}
                data-testid="normalize-preview-close"
                className="text-muted-foreground hover:text-myb-500 p-1"
                aria-label="Close"
              ><X size={16} weight="bold" /></button>
            </div>

            <div className="p-5 space-y-4">
              {normalizePreview.fields.map((f) => (
                <div key={f.field} className={`border hairline rounded-sm p-3 ${f.rows_to_update === 0 ? "bg-emerald-50/30 border-emerald-200" : "bg-white"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <code className="text-xs font-semibold text-foreground">{f.field}</code>
                    {f.rows_to_update === 0 ? (
                      <span className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Already clean</span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">
                        {fmtNumber(f.rows_to_update)} rows · {fmtNumber(f.distinct_dirty_values)} unique value{f.distinct_dirty_values === 1 ? "" : "s"}
                      </span>
                    )}
                  </div>
                  {f.samples.length > 0 && (
                    <div className="text-[11px] grid grid-cols-[1fr_auto_1fr_auto] items-center gap-x-3 gap-y-1 mt-2">
                      <span className="text-[9px] uppercase tracking-wider text-muted-foreground/60 font-semibold">Before</span>
                      <span></span>
                      <span className="text-[9px] uppercase tracking-wider text-muted-foreground/60 font-semibold">After</span>
                      <span className="text-[9px] uppercase tracking-wider text-muted-foreground/60 font-semibold text-right">Rows</span>
                      {f.samples.map((s, i) => (
                        <React.Fragment key={i}>
                          <code className="bg-rose-50 text-rose-700 px-1.5 py-0.5 rounded-sm font-mono">{s.before}</code>
                          <span className="text-muted-foreground">→</span>
                          <code className="bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-sm font-mono">{s.after}</code>
                          <span className="text-muted-foreground tabular-nums text-right">{fmtNumber(s.rows)}</span>
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex items-center justify-end gap-2 border-t hairline p-4 bg-myb-50/30">
              <button
                onClick={closeNormalizePreview}
                data-testid="normalize-preview-cancel"
                className="text-xs uppercase tracking-wider text-muted-foreground hover:text-myb-500 font-semibold px-3 py-2"
              >Cancel</button>
              <button
                onClick={doNormalizeCodes}
                disabled={normalizePreview.total_rows_affected === 0 || normalizeBusy}
                data-testid="normalize-preview-confirm"
                className="text-sm bg-amber-600 hover:bg-amber-700 disabled:bg-muted disabled:text-muted-foreground text-white px-4 py-2 rounded-sm inline-flex items-center gap-2 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Warning size={14} weight="bold" />
                {normalizePreview.total_rows_affected === 0
                  ? "Nothing to clean"
                  : `Confirm — clean ${fmtNumber(normalizePreview.total_rows_affected)} rows`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Upload;
